import React from "react";
import { motion } from "framer-motion";
import { Play, Pause, Heart, ShareNetwork } from "@phosphor-icons/react";
import { usePlayer } from "../context/PlayerContext";
import { flagEmoji, initials, safeFavicon } from "../lib/utils-radio";
import { shareStation } from "../lib/share";
import { Equalizer } from "./Equalizer";

export const StationCard = ({ station, index = 0 }) => {
  const { current, isPlaying, play, toggleFavorite, isFavorite } = usePlayer();
  const isCurrent = current?.stationuuid === station.stationuuid;
  const fav = isFavorite(station.stationuuid);
  const tags = (station.tags || "").split(",").filter(Boolean).slice(0, 2);
  const favicon = safeFavicon(station.favicon);

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.03, 0.5) }}
      data-testid={`station-card-${station.stationuuid}`}
      className={`group relative overflow-hidden rounded-xl border bg-surface p-4 transition-all duration-300 hover:-translate-y-1.5 hover:bg-surfaceHover hover:shadow-[0_8px_32px_rgba(229,178,93,0.10)] ${
        isCurrent ? "border-gold/60 shadow-[0_0_24px_rgba(229,178,93,0.15)]" : "border-border hover:border-white/20"
      }`}
    >
      <div className="flex items-start gap-4">
        <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-surfaceHover">
          {favicon ? (
            <img
              src={favicon}
              alt={station.name}
              className="h-full w-full object-cover"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : null}
          <div
            className="absolute inset-0 flex items-center justify-center font-heading text-lg font-semibold text-gold"
            style={{ display: favicon ? "none" : "flex" }}
          >
            {initials(station.name)}
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <h4 className="truncate font-heading text-base font-medium text-foreground" title={station.name}>
            {station.name?.trim() || "Unknown Station"}
          </h4>
          <div className="mt-1 flex items-center gap-2 font-mono text-xs text-muted2">
            <span>{flagEmoji(station.countrycode)}</span>
            <span className="truncate">{station.country || "Global"}</span>
            {station.bitrate ? <span className="text-gold/70">· {station.bitrate}k</span> : null}
          </div>
          {tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {tags.map((t) => (
                <span key={t} className="rounded-full border border-border bg-black/40 px-2 py-0.5 font-mono text-[10px] text-muted2">
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <button
          data-testid={`play-btn-${station.stationuuid}`}
          onClick={() => play(station)}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-gold text-black transition-transform hover:scale-105 active:scale-95 shadow-[0_0_15px_rgba(229,178,93,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
          aria-label={isCurrent && isPlaying ? "Pause" : "Play"}
        >
          {isCurrent && isPlaying ? (
            <Pause weight="fill" size={18} />
          ) : (
            <Play weight="fill" size={18} className="ml-0.5" />
          )}
        </button>

        <div className="flex items-center gap-2">
          {isCurrent && isPlaying && <Equalizer active />}
          <button
            data-testid={`share-btn-${station.stationuuid}`}
            onClick={() => shareStation(station)}
            className="text-muted2 transition-colors hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-full p-1"
            aria-label="Share station"
          >
            <ShareNetwork size={20} />
          </button>
          <button
            data-testid={`fav-btn-${station.stationuuid}`}
            onClick={() => toggleFavorite(station)}
            className="text-muted2 transition-colors hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-full p-1"
            aria-label={fav ? "Remove favorite" : "Add favorite"}
          >
            <Heart weight={fav ? "fill" : "regular"} size={22} className={fav ? "text-gold" : ""} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};
